import React, { useEffect, useState } from "react";
import {
  collection,
  query,
  orderBy,
  limit,
  getDocs,
} from "firebase/firestore";
import { db } from "../utils/firebase";
import "../../../styles/cms.css";

interface ActivityItem {
  id: string;
  source: "cms" | "global";
  project: string;
  action: string;
  contentType?: string;
  docId?: string;
  docTitle?: string;
  detail?: string;
  user: string;
  photoURL?: string;
  timestamp: Date | null;
}

const ACTION_LABELS: Record<string, { label: string; icon: string; color: string }> = {
  create: { label: "Created", icon: "+", color: "#16a34a" },
  edit: { label: "Edited", icon: "~", color: "#6366f1" },
  delete: { label: "Deleted", icon: "×", color: "#dc2626" },
  deploy: { label: "Deployed", icon: "↑", color: "#f59e0b" },
  translate: { label: "Translated", icon: "T", color: "#8b5cf6" },
  refund: { label: "Refund", icon: "$", color: "#ec4899" },
  cancel: { label: "Cancel", icon: "⊘", color: "#f97316" },
  login: { label: "Logged in", icon: "→", color: "#0ea5e9" },
  invite_accepted: { label: "Joined", icon: "✓", color: "#16a34a" },
};

const LIMIT = 500;

const GlobalActivity = () => {
  const [items, setItems] = useState<ActivityItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [avatarMap, setAvatarMap] = useState<Record<string, string>>({});

  useEffect(() => {
    const load = async () => {
      try {
        const all: ActivityItem[] = [];

        // 0. Load users for avatar + uid lookup
        const usersSnap = await getDocs(collection(db, "users"));
        const aMap: Record<string, string> = {};
        const uidMap: Record<string, { email: string; photoURL: string }> = {};
        usersSnap.forEach((d) => {
          const data = d.data();
          if (data.email && data.photoURL) aMap[data.email] = data.photoURL;
          uidMap[d.id] = { email: data.email || "", photoURL: data.photoURL || "" };
        });
        setAvatarMap(aMap);

        // 1. Load project list from content collection
        const projectsSnap = await getDocs(collection(db, "content"));
        const projectIds: string[] = [];
        projectsSnap.forEach((doc) => {
          if (doc.data().name) projectIds.push(doc.id);
        });

        // 2. Query each project's _activity subcollection
        const cmsPromises = projectIds.map((pid) =>
          getDocs(
            query(
              collection(db, "content", pid, "_activity"),
              orderBy("timestamp", "desc"),
              limit(LIMIT)
            )
          ).then((snap) => {
            snap.forEach((d) => {
              const data = d.data();
              all.push({
                id: `${pid}_${d.id}`,
                source: "cms",
                project: pid,
                action: data.action || "edit",
                contentType: data.contentType || "",
                docId: data.docId || "",
                docTitle: data.docTitle || "",
                detail: data.detail || "",
                user: data.user || "unknown",
                photoURL: data.photoURL || "",
                timestamp: data.timestamp?.toDate?.() || null,
              });
            });
          })
        );

        // 3. Query global activity log
        const globalPromise = getDocs(
          query(
            collection(db, "_activity_log"),
            orderBy("timestamp", "desc"),
            limit(LIMIT)
          )
        ).then((snap) => {
          snap.forEach((d) => {
            const data = d.data();
            const resolved = data.uid ? uidMap[data.uid] : null;
            all.push({
              id: `global_${d.id}`,
              source: "global",
              project: "",
              action: data.action || "action",
              detail: data.detail || "",
              user: resolved?.email || data.detail || data.user || "unknown",
              photoURL: resolved?.photoURL || data.photoURL || "",
              timestamp: data.timestamp?.toDate?.() || null,
            });
          });
        }).catch(() => {});

        await Promise.all([...cmsPromises, globalPromise]);

        // Sort by timestamp desc
        all.sort((a, b) => (b.timestamp?.getTime() || 0) - (a.timestamp?.getTime() || 0));

        setItems(all.slice(0, LIMIT));
      } catch (err) {
        console.error("[GlobalActivity] Error:", err);
      }
      setLoading(false);
    };
    load();
  }, []);

  const formatTime = (d: Date | null) => {
    if (!d) return "—";
    const now = new Date();
    const diff = now.getTime() - d.getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `${days}d ago`;
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };

  const formatDate = (d: Date | null) => {
    if (!d) return "";
    return d.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" });
  };

  // Group by date
  const grouped: { date: string; items: ActivityItem[] }[] = [];
  let currentDate = "";
  for (const item of items) {
    const dateStr = item.timestamp ? formatDate(item.timestamp) : "Unknown";
    if (dateStr !== currentDate) {
      currentDate = dateStr;
      grouped.push({ date: dateStr, items: [] });
    }
    grouped[grouped.length - 1].items.push(item);
  }

  const shortEmail = (email: string) => email.split("@")[0];

  return (
    <div style={{ maxWidth: 700, margin: "0 auto", padding: "24px 16px" }}>
      <h2 style={{ fontSize: "1.125rem", fontWeight: 600, margin: "0 0 20px" }}>Activity</h2>

      {loading ? (
        <div className="cms__loading">Loading...</div>
      ) : items.length === 0 ? (
        <div className="cms__empty">No activity yet.</div>
      ) : (
        <div className="cms__activity">
          {grouped.map((group) => (
            <div key={group.date} className="cms__activity-group">
              <div className="cms__activity-date">{group.date}</div>
              {group.items.map((item) => {
                const a = ACTION_LABELS[item.action] || ACTION_LABELS.edit;
                return (
                  <div key={item.id} className="cms__activity-item">
                    <span className="cms__activity-icon" style={{ background: a.color }}>{a.icon}</span>
                    <div className="cms__activity-body">
                      <span className="cms__activity-text">
                        {(item.photoURL || avatarMap[item.user]) && (
                          <img
                            className="cms__activity-avatar"
                            src={item.photoURL || avatarMap[item.user]}
                            alt=""
                            width="16"
                            height="16"
                            referrerPolicy="no-referrer"
                          />
                        )}
                        <strong>{shortEmail(item.user)}</strong>
                        {" "}
                        {item.action === "login" ? (
                          <>{a.label.toLowerCase()}</>
                        ) : item.source === "global" ? (
                          <>
                            {a.label.toLowerCase()}
                            {item.detail && <> <span className="cms__activity-target">{item.detail}</span></>}
                          </>
                        ) : item.action === "deploy" ? (
                          <>
                            deployed{item.detail && <> <span className="cms__activity-type">{item.detail}</span></>}
                            {item.project && <span className="cms__activity-project">{item.project}</span>}
                          </>
                        ) : (
                          <>
                            {a.label.toLowerCase()}{" "}
                            {item.contentType && <span className="cms__activity-type">{item.contentType}</span>}
                            {item.docTitle && <span className="cms__activity-target">{item.docTitle}</span>}
                            {!item.docTitle && item.docId && <span className="cms__activity-target">{item.docId}</span>}
                            {item.project && <span className="cms__activity-project">{item.project}</span>}
                          </>
                        )}
                      </span>
                    </div>
                    <span className="cms__activity-time">{formatTime(item.timestamp)}</span>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default GlobalActivity;
